import { useCallback, useEffect, useState } from 'react'
import { ShieldOff } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { Pill, fmtDate, label } from '@/features/dossier/components/primitives'
import { listSuppressions, releaseSuppression, suppress, type Suppression } from './api'

/**
 * Who must not be contacted.
 *
 * ## The address is not on this screen, and that is not an oversight
 *
 * Only a blind index of it is stored. The only question ever asked of this table is "is THIS
 * address on the list", which an index answers — and keeping the plaintext would make the
 * opt-out list, a list of people who explicitly asked to be left alone, the single most
 * attractive table in the database.
 *
 * ## Releasing is a separate authority and needs a written reason
 *
 * Adding somebody is routine. Taking them off puts a person who asked to be left alone back
 * within reach of a campaign, and that has to be attributable months later.
 */
export function SuppressionsPage() {
  const authorities = useSession((s) => s.authorities)
  const canRelease = hasPermission(authorities, 'comms:suppress-release')

  const [rows, setRows] = useState<Suppression[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [adding, setAdding] = useState(false)

  const [channelCode, setChannelCode] = useState('SMS')
  const [address, setAddress] = useState('')
  const [reasonCode, setReasonCode] = useState('MANUAL')
  const [note, setNote] = useState('')

  const load = useCallback(async () => {
    try {
      setError(null)
      setRows(await listSuppressions())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !rows) return <ErrorStrip message={error} />
  if (!rows) return <LoadingBlock label="Loading the list" />

  async function add(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true); setError(null)
    try {
      await suppress({ channelCode, address: address.trim(), reasonCode, note: note.trim() })
      setAdding(false); setAddress(''); setNote('')
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Do not contact"
        description="Addresses no campaign may reach. Checked when an audience is built and
                     again for every message, because people opt out overnight."
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="flex flex-wrap items-center justify-between gap-2 p-2">
          <p className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <ShieldOff className="size-3.5" />
            The register's own suppression covers voters and is enforced separately. This list
            is for everyone it does not reach — a number that replied STOP, an address that
            bounced, a request taken over the phone.
          </p>
          <button type="button" onClick={() => setAdding((v) => !v)}
                  className="rounded border border-border px-2 py-1 text-[11px]">
            {adding ? 'Close' : 'Add an address'}
          </button>
        </div>

        {adding && (
          <form onSubmit={add} className="space-y-2 border-t border-border p-3">
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Channel
                </span>
                <select value={channelCode} onChange={(e) => setChannelCode(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="SMS">SMS</option>
                  <option value="EMAIL">Email</option>
                </select>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Address
                </span>
                <input value={address} onChange={(e) => setAddress(e.target.value)} required
                       placeholder="0712345678"
                       className="h-7 rounded border border-input bg-background px-2
                                  text-[12px]" />
                <span className="text-[10px] text-muted-foreground">
                  Stored as an index, never in the clear.
                </span>
              </label>
              <label className="flex flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                  Why
                </span>
                <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                        className="h-7 rounded border border-input bg-background px-2
                                   text-[12px]">
                  <option value="MANUAL">Asked us directly</option>
                  <option value="COMPLAINT">Complained</option>
                  <option value="INVALID">Not a working number</option>
                  <option value="BOUNCED">Bounced</option>
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Note
              </span>
              <input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Called the office on 27 August and asked to be removed"
                     className="h-7 rounded border border-input bg-background px-2
                                text-[12px]" />
              <span className="text-[10px] text-muted-foreground">
                Required for a manual entry. One with no explanation cannot be reviewed or
                appealed.
              </span>
            </label>
            <button type="submit" disabled={busy || !address.trim()}
                    className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                               text-primary-foreground disabled:opacity-50">
              {busy ? 'Saving…' : 'Add to the list'}
            </button>
          </form>
        )}
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Channel</Th>
          <Th>Why</Th>
          <Th>Note</Th>
          <Th className="axis-hide-sm">Since</Th>
          <Th />
        </>}>
          {rows.length === 0 && (
            <EmptyRow colSpan={5}
                      message="Nobody has asked to be left alone. A STOP reply adds itself here
                               without waiting for anybody." />
          )}
          {rows.map((s) => (
            <tr key={s.id}>
              <Td><Pill className="border-border">{s.channelCode}</Pill></Td>
              <Td>
                <span className="text-[11px]">{label(s.reasonCode)}</span>
              </Td>
              <Td>
                <span className="text-[11px] text-muted-foreground">{s.note ?? '—'}</span>
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">
                  {fmtDate(s.suppressedAt)}
                </span>
              </Td>
              <Td>
                {canRelease && (
                  <button type="button" disabled={busy}
                          onClick={() => {
                            const reason = window.prompt(
                              'Why is this person being put back within reach of a campaign? '
                              + '(10 characters minimum — it stays on the record)')
                            if (reason) {
                              setBusy(true)
                              void releaseSuppression(s.id, reason)
                                .then(load)
                                .catch((err) => setError(explain(err)))
                                .finally(() => setBusy(false))
                            }
                          }}
                          className="whitespace-nowrap rounded border border-border px-2
                                     py-0.5 text-[10px]">
                    Release
                  </button>
                )}
              </Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
